import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Sheet,
    Typography,
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Alert,
    CircularProgress,
    Breadcrumbs,
    Link,
    Divider,
    Select,
    Option,
    RadioGroup,
    Radio
} from '@mui/joy';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import domain from '../utils/domain';

interface Question {
    id?: number;
    section_id: number;
    question_text: string;
    question_type: 'MCQ' | 'TRUE_FALSE';
    difficulty_level: 'easy' | 'medium' | 'hard';
    option_a: string;
    option_b: string;
    option_c?: string;
    option_d?: string;
    correct_answer: number;
    explanation?: string;
    image_url?: string;
}

interface Section {
    id: number;
    name: string;
}

const EditQuestion: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNewQuestion = !id;

    const [question, setQuestion] = useState<Question>({
        section_id: 0,
        question_text: '',
        question_type: 'MCQ',
        difficulty_level: 'medium',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_answer: 0,
        explanation: '',
        image_url: ''
    });

    const [sections, setSections] = useState<Section[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchSections();
        if (!isNewQuestion) {
            fetchQuestion();
        }
    }, [id]);

    const fetchSections = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/sections`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch sections');
            }

            const data = await response.json();
            setSections(data.sections);

            // Set default section_id if it's a new question
            if (isNewQuestion && data.sections.length > 0) {
                setQuestion(prev => ({
                    ...prev,
                    section_id: data.sections[0].id
                }));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load sections');
        }
    };

    const fetchQuestion = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/questions/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch question');
            }

            const data = await response.json();
            setQuestion(data.question);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load question');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const url = isNewQuestion
                ? `${domain}/api/admin/questions`
                : `${domain}/api/admin/questions/${id}`;

            const response = await fetch(url, {
                method: isNewQuestion ? 'POST' : 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(question)
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to save question');
            }

            navigate('/admin/dashboard?tab=questions');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save question');
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setQuestion(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelectChange = (
        name: string,
        value: string | null
    ) => {
        if (value !== null) {
            setQuestion(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.level1' }}>
            <Sheet
                variant="plain"
                sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'white',
                    borderBottom: '2px solid',
                    borderColor: '#ea7a23',
                    color: '#ea7a23'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                        alt="Vector India Logo"
                        style={{
                            width: '200px',
                            height: 'auto'
                        }}
                    />
                    <Typography level="h4" sx={{ color: '#ea7a23' }}>
                        {isNewQuestion ? 'Create New Question' : 'Edit Question'}
                    </Typography>
                </Box>
            </Sheet>

            <Box sx={{ p: 2, maxWidth: '800px', margin: '0 auto' }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link href="/admin/dashboard">Dashboard</Link>
                    <Link href="/admin/dashboard?tab=questions">Questions</Link>
                    <Typography>{isNewQuestion ? 'Create New Question' : 'Edit Question'}</Typography>
                </Breadcrumbs>

                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Sheet
                    variant="outlined"
                    sx={{
                        borderRadius: 'sm',
                        p: 3,
                        mb: 3
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        <FormControl required sx={{ mb: 2 }}>
                            <FormLabel>Section</FormLabel>
                            <Select
                                value={question.section_id.toString()}
                                onChange={(_, value) => handleSelectChange('section_id', value)}
                                required
                            >
                                {sections.map((section) => (
                                    <Option key={section.id} value={section.id.toString()}>
                                        {section.name}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl required sx={{ mb: 2 }}>
                            <FormLabel>Question Type</FormLabel>
                            <Select
                                value={question.question_type}
                                onChange={(_, value) => handleSelectChange('question_type', value)}
                                required
                            >
                                <Option value="MCQ">Multiple Choice</Option>
                                <Option value="TRUE_FALSE">True/False</Option>
                            </Select>
                        </FormControl>

                        <FormControl required sx={{ mb: 2 }}>
                            <FormLabel>Difficulty Level</FormLabel>
                            <Select
                                value={question.difficulty_level}
                                onChange={(_, value) => handleSelectChange('difficulty_level', value)}
                                required
                            >
                                <Option value="easy">Easy</Option>
                                <Option value="medium">Medium</Option>
                                <Option value="hard">Hard</Option>
                            </Select>
                        </FormControl>

                        <FormControl required sx={{ mb: 2 }}>
                            <FormLabel>Question Text</FormLabel>
                            <Textarea
                                name="question_text"
                                value={question.question_text}
                                onChange={handleInputChange}
                                minRows={3}
                                required
                                placeholder="Enter your question"
                            />
                        </FormControl>

                        {question.question_type === 'MCQ' ? (
                            <>
                                <FormControl required sx={{ mb: 2 }}>
                                    <FormLabel>Option A</FormLabel>
                                    <Input
                                        name="option_a"
                                        value={question.option_a}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormControl>

                                <FormControl required sx={{ mb: 2 }}>
                                    <FormLabel>Option B</FormLabel>
                                    <Input
                                        name="option_b"
                                        value={question.option_b}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormControl>

                                <FormControl required sx={{ mb: 2 }}>
                                    <FormLabel>Option C</FormLabel>
                                    <Input
                                        name="option_c"
                                        value={question.option_c}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormControl>

                                <FormControl required sx={{ mb: 2 }}>
                                    <FormLabel>Option D</FormLabel>
                                    <Input
                                        name="option_d"
                                        value={question.option_d}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormControl>

                                <FormControl required sx={{ mb: 2 }}>
                                    <FormLabel>Correct Answer</FormLabel>
                                    <RadioGroup
                                        value={question.correct_answer.toString()}
                                        onChange={(e) => handleSelectChange('correct_answer', e.target.value)}
                                    >
                                        <Radio value="1" label="Option A" />
                                        <Radio value="2" label="Option B" />
                                        <Radio value="3" label="Option C" />
                                        <Radio value="4" label="Option D" />
                                    </RadioGroup>
                                </FormControl>
                            </>
                        ) : (
                            <FormControl required sx={{ mb: 2 }}>
                                <FormLabel>Correct Answer</FormLabel>
                                <RadioGroup
                                    value={question.correct_answer.toString()}
                                    onChange={(e) => handleSelectChange('correct_answer', e.target.value)}
                                >
                                    <Radio value="1" label="True" />
                                    <Radio value="2" label="False" />
                                </RadioGroup>
                            </FormControl>
                        )}

                        <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Explanation</FormLabel>
                            <Textarea
                                name="explanation"
                                value={question.explanation}
                                onChange={handleInputChange}
                                minRows={2}
                                placeholder="Explain the correct answer (optional)"
                            />
                        </FormControl>

                        <FormControl sx={{ mb: 3 }}>
                            <FormLabel>Image URL</FormLabel>
                            <Input
                                name="image_url"
                                value={question.image_url}
                                onChange={handleInputChange}
                                placeholder="Enter image URL (optional)"
                            />
                        </FormControl>

                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                onClick={() => navigate('/admin/dashboard?tab=questions')}
                                startDecorator={<CancelIcon />}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={isSaving}
                                startDecorator={<SaveIcon />}
                            >
                                {isNewQuestion ? 'Create Question' : 'Save Changes'}
                            </Button>
                        </Box>
                    </form>
                </Sheet>
            </Box>
        </Box>
    );
};

export default EditQuestion; 